import React, { useState, useCallback } from 'react';
import { useToast } from './Toast/ToastContainer';
import { subscriberService } from '../services/subscriberService';
import './NewsletterForm.css';

interface NewsletterFormProps {
  variant?: 'inline' | 'card';
  onSuccess?: () => void;
}

type SubscriptionStatus = 'idle' | 'loading' | 'success' | 'error' | 'already_subscribed';

export function NewsletterForm({ variant = 'inline', onSuccess }: NewsletterFormProps) {
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<SubscriptionStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const validateEmail = useCallback((email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      showToast({
        type: 'warning',
        title: 'Email requerido',
        message: 'Por favor ingresa tu email',
      });
      return;
    }

    if (!validateEmail(email)) {
      showToast({
        type: 'warning',
        title: 'Email inválido',
        message: 'Por favor ingresa un email válido',
      });
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      await subscriberService.createSubscriber({ email });
      setStatus('success');
      showToast({
        type: 'success',
        title: '¡Suscripción exitosa!',
        message: 'Gracias por suscribirte a nuestro newsletter',
      });
      setEmail('');
      onSuccess?.();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      if (errorMessage.includes('ya está registrado')) {
        setStatus('already_subscribed');
        showToast({
          type: 'info',
          title: 'Ya estás suscrito',
          message: 'Este email ya está registrado en nuestro newsletter',
        });
      } else {
        setStatus('error');
        setErrorMessage(errorMessage);
        showToast({
          type: 'error',
          title: 'Error',
          message: 'No se pudo completar la suscripción. Intenta de nuevo.',
        });
      }
    }
  }, [email, validateEmail, showToast, onSuccess]);

  if (variant === 'card') {
    return (
      <div className="newsletter-card">
        <div className="newsletter-card-content">
          <h3 className="newsletter-card-title">Suscríbete a nuestro Newsletter</h3>
          <p className="newsletter-card-description">
            Recibe las últimas noticias, actualizaciones y contenido exclusivo directamente en tu email.
          </p>
        </div>
        <form className="newsletter-form" onSubmit={handleSubmit}>
          <div className="newsletter-input-group">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              className="newsletter-input"
              disabled={status === 'loading'}
              aria-label="Email para suscripción"
            />
            <button
              type="submit"
              className="newsletter-button"
              disabled={status === 'loading'}
            >
              {status === 'loading' ? (
                <span className="newsletter-spinner"></span>
              ) : (
                'Suscribirse'
              )}
            </button>
          </div>
          {status === 'success' && (
            <p className="newsletter-message success">¡Gracias por suscribirte!</p>
          )}
          {status === 'already_subscribed' && (
            <p className="newsletter-message info">Ya estás suscrito con este email</p>
          )}
          {status === 'error' && errorMessage && (
            <p className="newsletter-message error">{errorMessage}</p>
          )}
        </form>
      </div>
    );
  }

  return (
    <form className="newsletter-inline" onSubmit={handleSubmit}>
      <div className="newsletter-inline-content">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Ingresa tu email"
          className="newsletter-inline-input"
          disabled={status === 'loading'}
          aria-label="Email para suscripción"
        />
        <button
          type="submit"
          className="newsletter-inline-button"
          disabled={status === 'loading'}
        >
          {status === 'loading' ? (
            <span className="newsletter-spinner"></span>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              Suscribirse
            </>
          )}
        </button>
      </div>
      {status === 'success' && (
        <p className="newsletter-inline-message success">¡Gracias por suscribirte!</p>
      )}
      {status === 'already_subscribed' && (
        <p className="newsletter-inline-message info">Ya estás suscrito con este email</p>
      )}
      {status === 'error' && errorMessage && (
        <p className="newsletter-inline-message error">{errorMessage}</p>
      )}
    </form>
  );
}

export default NewsletterForm;